import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  Ticket, Plus, RefreshCw, Loader2, Infinity as Infty,
} from "lucide-react";

/**
 * SuperAdmin — Referral Codes management.
 *
 * Renders the read-only list for any admin (list endpoint is gated at
 * require_admin) but the CREATE and TOGGLE mutations are gated to
 * require_superadmin server-side. We still render the UI for these
 * actions to any admin who lands here — the backend will 403 and
 * we surface that as an inline toast.
 */
export default function SuperAdminReferralCodesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    code: "", duration_days: 30, max_uses: "", campaign_limit: "", note: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.adminListReferralCodes({ page: 1, page_size: 100 });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn’t load referral codes.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (busy) return;
    const payload = {
      code: form.code.trim().toUpperCase(),
      duration_days: Number(form.duration_days),
      max_uses: form.max_uses === "" ? null : Number(form.max_uses),
      campaign_limit: form.campaign_limit === "" ? null : Number(form.campaign_limit),
      note: form.note.trim() || null,
    };
    if (!payload.code || payload.code.length < 2) {
      toast.error("Code must be at least 2 characters."); return;
    }
    if (!payload.duration_days || payload.duration_days < 1) {
      toast.error("Duration must be at least 1 day."); return;
    }
    setBusy(true);
    try {
      await api.saCreateReferralCode(payload);
      toast.success(`Created ${payload.code}`);
      setForm({ code: "", duration_days: 30, max_uses: "", campaign_limit: "", note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn’t create referral code.");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.saUpdateReferralCode(row.id, { is_active: !row.is_active });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn’t update code.");
    }
  };

  return (
    <div className="space-y-6" data-testid="sa-referral-codes-page">
      <Toaster richColors position="top-center" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Ticket className="h-6 w-6" />
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Referral codes</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Signup-time codes that grant a time-limited free access window with an optional campaign cap.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="sa-refcodes-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <Card className="p-5 md:p-6">
        <h2 className="font-bold mb-4 flex items-center gap-2"><Plus className="h-4 w-4" /> Create referral code</h2>
        <form onSubmit={onCreate} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div className="space-y-1.5">
            <Label>Code</Label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SUMMER30" className="uppercase tracking-wider" data-testid="sa-refcode-code" />
          </div>
          <div className="space-y-1.5">
            <Label>Duration (days)</Label>
            <Input type="number" min="1" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} data-testid="sa-refcode-days" />
          </div>
          <div className="space-y-1.5">
            <Label>Max uses <span className="text-muted-foreground text-xs">(blank = unlimited)</span></Label>
            <Input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="∞" data-testid="sa-refcode-maxuses" />
          </div>
          <div className="space-y-1.5">
            <Label>Campaign cap <span className="text-muted-foreground text-xs">(blank = unlimited)</span></Label>
            <Input type="number" min="1" value={form.campaign_limit} onChange={(e) => setForm({ ...form, campaign_limit: e.target.value })} placeholder="∞" data-testid="sa-refcode-campcap" />
          </div>
          <div className="space-y-1.5 lg:col-span-5">
            <Label>Note (optional)</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Internal note for accounting/audit" data-testid="sa-refcode-note" />
          </div>
          <div className="lg:col-span-5">
            <Button type="submit" disabled={busy} data-testid="sa-refcode-create">
              {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Creating…</> : <><Plus className="h-4 w-4 mr-1.5" /> Create code</>}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3"><Skeleton className="h-6 w-full" /><Skeleton className="h-6 w-full" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="sa-refcodes-empty">No referral codes yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sa-refcodes-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground bg-secondary/40">
                  <th className="py-3 px-4">Code</th>
                  <th className="py-3 px-4">Duration</th>
                  <th className="py-3 px-4">Usage</th>
                  <th className="py-3 px-4">Campaign cap</th>
                  <th className="py-3 px-4">Note</th>
                  <th className="py-3 px-4">Created</th>
                  <th className="py-3 px-4">Active</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`sa-refcode-row-${r.id}`}>
                    <td className="py-3 px-4 font-mono font-semibold">{r.code}</td>
                    <td className="py-3 px-4">{r.duration_days}d</td>
                    <td className="py-3 px-4 tabular-nums">
                      {r.max_uses == null
                        ? <span className="inline-flex items-center gap-1">{r.uses_count} <Infty className="h-3 w-3 opacity-60" /></span>
                        : <>{r.uses_count} / {r.max_uses}</>}
                    </td>
                    <td className="py-3 px-4 tabular-nums">{r.campaign_limit == null ? <Infty className="h-4 w-4 opacity-60" /> : r.campaign_limit}</td>
                    <td className="py-3 px-4 text-muted-foreground max-w-[280px] truncate">{r.note || "—"}</td>
                    <td className="py-3 px-4 text-muted-foreground text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Switch checked={!!r.is_active} onCheckedChange={() => toggleActive(r)} data-testid={`sa-refcode-toggle-${r.id}`} />
                        <Badge variant="outline" className={r.is_active ? "bg-emerald-100 text-emerald-900 border-emerald-300" : ""}>
                          {r.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
